<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="reservations"
      item-key="_id"
      :loading="loading"
      class="elevation-1"
    >
      <template #progress>
        <v-progress-linear color="red" indeterminate />
      </template>

      <template #item="{ item }">
        <!-- Main Row -->
        <tr
          :class="isExpanded(item) ? 'tableSelected pointer' : 'pointer'"
          @click.stop="toggleExpand(item)"
        >
          <!-- Expand button -->

          <!-- Status -->
          <td class="pa-1">
            <v-chip
              v-if="item.status === 'Active'"
              label
              variant="outlined"
              color="primary"
            >
              {{ $t("reservations.datatable.active") }}
            </v-chip>
            <v-chip
              v-if="item.status === 'Sold'"
              label
              variant="outlined"
              color="success"
            >
              {{ $t("reservations.datatable.sold") }}
            </v-chip>
            <v-chip
              v-if="item.status === 'Expired'"
              label
              variant="outlined"
              color="error"
            >
              {{ $t("reservations.datatable.expired") }}
            </v-chip>
            <v-chip
              v-if="item.status === 'Cancelled'"
              label
              variant="outlined"
              color="error"
            >
              {{ $t("reservations.datatable.cancelled") }}
            </v-chip>
          </td>

          <!-- Customer -->
          <td class="text-xs">
            <span v-if="item.client_id">
              {{ item.client_id.name }} {{ item.client_id.surname }}
            </span>
          </td>

          <!-- Comment -->
          <td class="text-xs">{{ item.comment }}</td>

          <!-- Price -->
          <td class="text-xs">{{ item.proposed_price }} {{ item.currency }}</td>

          <!-- Date -->
          <td>
            <span v-if="$props.type === 'active'" class="text-xs">
              {{ dateFromNow(item.date_created) }}
            </span>
            <span v-else-if="$props.type === 'inactive'" class="text-xs">
              {{ formatDate(item.date_created) }}
            </span>
          </td>

          <!-- Actions -->
          <td class="text-xs">
            <v-row class="d-flex flex-nowrap">
              <v-btn
                v-if="$props.type === 'active'"
                icon
                variant="text"
                @click.stop="sell(item._id)"
              >
                <v-icon color="grey-darken-2">mdi-check</v-icon>
              </v-btn>
              <v-btn
                v-if="$props.type === 'active'"
                icon
                variant="text"
                @click.stop="cancelReservation(item._id)"
              >
                <v-icon color="grey-darken-2">mdi-cancel</v-icon>
              </v-btn>
              <v-btn
                v-if="$props.type === 'inactive'"
                icon
                variant="text"
                @click.stop="deleteReservation(item._id)"
              >
                <v-icon color="grey-darken-2">mdi-delete</v-icon>
              </v-btn>
            </v-row>
          </td>
          <td>
            <v-btn icon variant="text" @click.stop="toggleExpand(item)">
              <v-icon>{{
                isExpanded(item) ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
            </v-btn>
          </td>
        </tr>

        <!-- Expanded Row -->
        <tr v-if="isExpanded(item)">
          <td :colspan="headers.length">
            <!-- Inventory Parts -->
            <v-toolbar dense color="red-darken-3">
              <v-toolbar-title>
                {{ $t("reservations.datatable.inventory_parts") }}
              </v-toolbar-title>
            </v-toolbar>

            <v-data-table
              :headers="partsHeaders"
              :items="item.parts"
              item-key="_id"
              class="nowrap-headers"
              :items-per-page-options="[5, 10, 25, 50]"
              items-per-page-text="Rows per page"
            >
              <template #item="{ item: part }">
                <td>
                  <div v-if="part.id.old_id">
                    <qr-code :size="60" :text="part.id.old_id" />
                    {{ part.id.old_id.slice(-6) }}
                  </div>
                  <div v-else>
                    <qr-code :size="60" :text="part.id._id" />
                    {{ part.id._id.slice(-6) }}
                  </div>
                </td>
                <td>
                  <v-btn icon variant="text" @click="details(part._id)">
                    <v-icon color="grey-darken-2">mdi-view-list</v-icon>
                  </v-btn>
                </td>
                <td>
                  {{
                    part.id.infoCar?.description?.descriptionId?.category?.[
                      $store.getters.language
                    ]
                  }}
                </td>
                <td>
                  {{
                    part.id.infoCar?.description?.descriptionId?.title?.[
                      $store.getters.language
                    ]
                  }}
                </td>
                <td>{{ part.id.manufacturer_code }}</td>
                <td>
                  <img
                    :src="
                      part.id.images?.[0]?.thumb
                        ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${part.id.images[0].thumb}`
                        : 'https://portal.lekotech.com/logo_small.png'
                    "
                    class="ma-1 pointer"
                    height="75"
                    width="100"
                    @click="showImages(part.id.images)"
                  />
                </td>
                <td>{{ part.id.notes }}</td>
                <td>{{ part.id.price }} {{ part.id.currency }}</td>
                <td>
                  {{
                    part.id.shelf_data
                      ? `${part.id.shelf_data.shelf.room_name} - ${part.id.shelf_data.shelf.location_code}: ${part.id.shelf_data.shelf.x}, ${part.id.shelf_data.shelf.y}`
                      : "-"
                  }}
                </td>
                <td>
                  <v-rating
                    v-if="part.id.quality"
                    :model-value="part.id.quality.rating"
                    color="red"
                    background-color="red-darken-4"
                    readonly
                    size="small"
                  />
                </td>
                <td>
                  <v-btn variant="outlined" color="black" size="small">
                    {{ formatDate(part.id.date_created) }}
                  </v-btn>
                </td>
              </template>
            </v-data-table>

            <!-- Reservation Parts -->
            <v-toolbar
              v-if="!($props.type === 'inactive' && item.status === 'Expired')"
              dense
              color="red-darken-3"
            >
              <v-toolbar-title>
                {{ $t("reservations.datatable.reservation_parts") }}
              </v-toolbar-title>
            </v-toolbar>

            <v-data-table
              v-if="
                item.reservationParts &&
                !($props.type === 'inactive' && item.status === 'Expired')
              "
              :headers="reservationPartsHeaders"
              :items="item.reservationParts"
              item-key="_id"
              class=""
              :items-per-page-options="[5, 10, 25, 50]"
              items-per-page-text="Rows per page"
            >
              <template #item="{ item: part }">
                <td>{{ part.description }}</td>
                <td>{{ part.notes }}</td>
                <td>
                  <img
                    :src="`${server}/images/reservation?q=${part._id}-${part.images[0]}`"
                    class="ma-1 pointer"
                    height="75"
                    width="100"
                    @click="showResImages(part._id, part.images)"
                  />
                </td>
                <td>{{ part.price }} {{ part.currency }}</td>
              </template>
            </v-data-table>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog v-model="sellDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :model-value="true"
            type="success"
            border="left"
            text
          >
            {{ $t("reservations.confirm_sell") }}
          </v-alert>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="sellDialog = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="success" variant="outlined" @click="confirmSell">
            {{ $t("parts.inventory.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="cancelDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :model-value="true"
            type="error"
            border="left"
            text
          >
            {{ $t("reservations.confirm_cancel") }}
          </v-alert>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelDialog = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmCancel">
            {{ $t("parts.inventory.actions.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :model-value="true"
            type="error"
            border="left"
            text
          >
            {{ $t("reservations.confirm_delete") }}
          </v-alert>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmDelete">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";
import postRequest from "../../api/postRequest";
import deleteRequest from "../../api/deleteRequest";

export default {
  name: "ReservationsDatatable",
  components: {
    "qr-code": VueQrCode,
  },
  props: {
    type: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      expandedItems: [],
      headers: [
        { title: "Status", sortable: false, key: "status" },
        { title: "Customer", sortable: false, key: "customer" },
        { title: "Comment", sortable: false, key: "comment" },
        { title: "Price", sortable: false, key: "price" },
        { title: "Date", sortable: true, key: "date" },
        { title: "Actions", sortable: false, key: "actions" },
        { title: "", key: "data-table-expand" },
      ],
      partsHeaders: [
        { title: "QR Code", sortable: true, key: "_id" },
        { title: "Actions", sortable: false, key: "actions" },
        { title: "Group", sortable: true, key: "group" },
        { title: "Title", sortable: true, key: "title" },
        {
          title: "Manufacturer Code",
          sortable: false,
          key: "manufacturer_code",
        },
        { title: "Images", sortable: false, key: "img" },
        { title: "Notes", sortable: false, key: "notes" },
        { title: "Price", sortable: true, key: "price" },
        { title: "Location", sortable: true, key: "shelf_data" },
        { title: "Quality", sortable: true, key: "quality" },
        { title: "Date Created", sortable: true, key: "date" },
      ],
      reservationPartsHeaders: [
        { title: "Description", sortable: false, key: "description" },
        { title: "Notes", sortable: false, key: "notes" },
        { title: "Images", sortable: false, key: "img" },
        { title: "Price", sortable: false, key: "price" },
      ],
      reservations: [],
      sellDialog: false,
      cancelDialog: false,
      deleteDialog: false,
      itemToSell: null,
      itemToCancel: null,
      itemToDelete: null,
    };
  },
  computed: {
    server() {
      return this.$serverUri;
    },
  },
  mounted() {
    this.getLocale();
    this.getReservations();
  },
  methods: {
    dateFromNow(dateCreated) {
      const creationTime = new Date(dateCreated).getTime();
      const expireTime = creationTime + 259200000; // 72 hours after creation
      const expireDate = new Date(expireTime);
      moment.locale(localStorage.getItem("language"));
      const result = moment(expireDate).fromNow();
      return result;
    },
    formatDate(dateCreated) {
      moment.locale(localStorage.getItem("language"));
      const result = moment(dateCreated).format("LLL");
      return result;
    },
    getLocale() {
      this.headers[0].title = this.$t("reservations.datatable.status");
      this.headers[1].title = this.$t("reservations.datatable.customer");
      this.headers[2].title = this.$t("reservations.datatable.comment");
      this.headers[3].title = this.$t("reservations.datatable.price");
      this.headers[4].title =
        this.$props.type === "active"
          ? this.$t("reservations.datatable.expiring_time")
          : this.$t("reservations.datatable.date_created");
      this.headers[5].title = this.$t("reservations.datatable.actions");
      this.partsHeaders[0].title = this.$t(
        "reservations.parts.datatable.qr_code",
      );
      this.partsHeaders[1].title = this.$t(
        "reservations.parts.datatable.actions",
      );
      this.partsHeaders[2].title = this.$t(
        "reservations.parts.datatable.group",
      );
      this.partsHeaders[3].title = this.$t(
        "reservations.parts.datatable.title",
      );
      this.partsHeaders[4].title = this.$t(
        "reservations.parts.datatable.manufacturer_code",
      );
      this.partsHeaders[5].title = this.$t(
        "reservations.parts.datatable.images",
      );
      this.partsHeaders[6].title = this.$t(
        "reservations.parts.datatable.notes",
      );
      this.partsHeaders[7].title = this.$t(
        "reservations.parts.datatable.price",
      );
      this.partsHeaders[8].title = this.$t(
        "reservations.parts.datatable.location",
      );
      this.partsHeaders[9].title = this.$t(
        "reservations.parts.datatable.quality",
      );
      this.partsHeaders[10].title = this.$t(
        "reservations.parts.datatable.date_created",
      );
      this.reservationPartsHeaders[0].title = this.$t(
        "reservations.datatable.description",
      );
      this.reservationPartsHeaders[1].title = this.$t(
        "reservations.datatable.notes",
      );
      this.reservationPartsHeaders[2].title = this.$t(
        "reservations.datatable.images",
      );
      this.reservationPartsHeaders[3].title = this.$t(
        "reservations.datatable.price",
      );
    },
    async getReservations() {
      try {
        this.loading = true;
        const response = await getRequest(
          `/reservation/${this.$props.type}`,
          `role=${this.$store.getters.role}&user_id=${this.$store.getters.userId}&institution_id=${this.$store.getters.institutionId}`,
        );
        this.reservations = response.reservations || [];
      } catch {
        this.$emit("error", "Failed to load reservations");
      } finally {
        this.loading = false;
      }
    },
    showImages(images) {
      this.$emit("images", { type: "string", images });
    },
    showResImages(id, images) {
      this.$emit("images", {
        type: "string",
        id,
        images,
        path: "reservations",
      });
    },
    details(data) {
      this.$emit("details", data);
    },
    sell(id) {
      this.itemToSell = id;
      this.sellDialog = true;
    },
    async confirmSell() {
      try {
        this.loading = true;
        const response = await postRequest.object("/reservation/sell", {
          id: this.itemToSell,
        });
        if (response) {
          this.$emit("sold");
          this.sellDialog = false;
          this.itemToSell = null;
          await this.getReservations();
        }
      } catch {
        this.$emit("error", "Failed to sell reservation");
      } finally {
        this.loading = false;
      }
    },
    cancelReservation(id) {
      this.itemToCancel = id;
      this.cancelDialog = true;
    },
    async confirmCancel() {
      try {
        this.loading = true;
        const response = await postRequest.object("/reservation/cancel", {
          id: this.itemToCancel,
        });
        if (response) {
          this.cancelDialog = false;
          this.itemToCancel = null;
          await this.getReservations();
        }
      } catch {
        this.$emit("error", "Failed to cancel reservation");
      } finally {
        this.loading = false;
      }
    },
    deleteReservation(id) {
      this.itemToDelete = id;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      try {
        this.loading = true;
        const response = await deleteRequest(
          "/reservation",
          `id=${this.itemToDelete}`,
        );
        if (response) {
          this.itemToDelete = null;
          this.deleteDialog = false;
          this.$emit("deleted");
          await this.getReservations();
        }
      } catch {
        this.$emit("error", "Failed to delete reservation");
      } finally {
        this.loading = false;
      }
    },
    getPosition(code) {
      switch (code) {
        case "S001":
          return this.$t("left");
        case "S002":
          return this.$t("right");
        default:
          return "";
      }
    },
    toggleExpand(item) {
      const key = item._id;
      const index = this.expandedItems.indexOf(key);
      if (index === -1) {
        this.expandedItems.push(key);
      } else {
        this.expandedItems.splice(index, 1);
      }
    },
    isExpanded(item) {
      return this.expandedItems.includes(item._id);
    },
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
.tableSelected {
  background-color: #f5f5f5;
}
.tableBorder {
  border: 1px solid #e0e0e0;
}
.nowrap-headers th {
  white-space: nowrap;
}
.text-xs {
  font-size: 0.75rem;
}
</style>
